import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { SpectrogramPoint } from '@ledium/types';
import { autoMixSpectra, calculateRelativeSpectrogramRanges } from '@ledium-mec/shared-utils';
import { Button, Grid } from '@mui/material';

import SpectrumMixerChannelSettingsCarousel from './SpectrumMixerChannelSettingsCarousel';
import SpectrumMixerChannelSettingsGrid from './SpectrumMixerChannelSettingsGrid';
import { MixerChannel, MixerSettings } from './SpectrumMixerChannelSettingsItem';

const SpectrumMixerChannelSettings = () => {
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const lightChannels = formData.lightChannels as MixerChannel[];
    const settings = formData.settings as MixerSettings;
    const lightRecipe = formData.lightRecipe as { spectrogram?: SpectrogramPoint[] } | null;
    const hasRecipe = !!lightRecipe?.spectrogram?.length;

    const lightChannelsRef = React.useRef(lightChannels);
    lightChannelsRef.current = lightChannels;
    const settingsRef = React.useRef(settings);
    settingsRef.current = settings;
    const pageActionsRef = React.useRef(pageActions);
    pageActionsRef.current = pageActions;
    const lightRecipeRef = React.useRef(lightRecipe);
    lightRecipeRef.current = lightRecipe;
    const onSliderChange = React.useCallback((channelId: string, value: number) => {
        const channels = lightChannelsRef.current;
        const updated = channels.map((channel) => {
            if (channel.id === channelId) {
                const channelMaxIntensity = 2 ** channel.resolution - 1;
                const intensityRatio = value / channelMaxIntensity;
                return {
                    ...channel,
                    intensityPercent: Math.round(intensityRatio * 100),
                    intensity: Math.round(+value),
                    intensityRanges: calculateRelativeSpectrogramRanges(channel.spectrogramRanges, intensityRatio)
                };
            }
            return channel;
        });
        pageActionsRef.current.formData.updateFormItem({ lightChannels: updated });
    }, []);

    const onIntensityChange = React.useCallback((channelId: string, valueParam: number) => {
        let value = valueParam;
        const channels = lightChannelsRef.current;
        const updated = channels.map((channel) => {
            if (channel.id === channelId) {
                const unitMax = channel.spectrogramRanges[settingsRef.current.intensityUnit].sum;
                const channelMaxIntensity = 2 ** channel.resolution - 1;
                value = value < 0 ? 0 : value;
                value = value > unitMax || (channel.resolution === 1 && value > 0) ? unitMax : value;
                const intensityRatio = value / unitMax;

                return {
                    ...channel,
                    intensityPercent: Math.round(intensityRatio * 100),
                    intensity: Math.round(channelMaxIntensity * intensityRatio),
                    intensityRanges: calculateRelativeSpectrogramRanges(channel.spectrogramRanges, intensityRatio)
                };
            }
            return channel;
        });
        pageActionsRef.current.formData.updateFormItem({ lightChannels: updated });
    }, []);

    const handleRecalculate = React.useCallback(() => {
        const recipe = lightRecipeRef.current?.spectrogram;
        if (!recipe) {
            return;
        }
        const channels = lightChannelsRef.current;
        const ratios = autoMixSpectra(recipe, channels, settingsRef.current.intensityUnit);
        const updated = channels.map((channel, i) => {
            const intensityRatio = ratios[i];
            const channelMaxIntensity = 2 ** channel.resolution - 1;
            return {
                ...channel,
                intensityPercent: Math.round(intensityRatio * 100),
                intensity: Math.round(channelMaxIntensity * intensityRatio),
                intensityRanges: calculateRelativeSpectrogramRanges(channel.spectrogramRanges, intensityRatio)
            };
        });
        pageActionsRef.current.formData.updateFormItem({ lightChannels: updated });
    }, []);

    if (!lightChannels.length) {
        return null;
    }

    return (
        <>
            {!formData.readOnly && hasRecipe && (
                <Grid item xs={12} sx={{ textAlign: 'center' }}>
                    <Button variant="outlined" onClick={handleRecalculate}>
                        {i18n('spectrum_mix_auto_mix') as string}
                    </Button>
                </Grid>
            )}
            {settings.carouselView ? (
                <SpectrumMixerChannelSettingsCarousel onSliderChange={onSliderChange} onIntensityChange={onIntensityChange} />
            ) : (
                <SpectrumMixerChannelSettingsGrid
                    lightChannels={lightChannels}
                    onSliderChange={onSliderChange}
                    onIntensityChange={onIntensityChange}
                    formData={formData}
                />
            )}
        </>
    );
};

export default React.memo(SpectrumMixerChannelSettings);
